import React, { useState } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../config/firebase';
import logoSvg from '../assets/logo.svg';

/**
 * Inline login screen for Account Portal.
 *
 * Visual contract matches the Rascal-UI-Kit standard login pattern
 * (see Rascal-UI-Kit/docs/DESIGN_SYSTEM.md §9 "Login screen") — same
 * two-layer composition as EPR + OKR Tracker:
 *   - Brand block: white card with #011132-bordered logo container
 *   - Sign-in card: gradient navy header + form body + footer
 *   - Inputs: px-4 py-3, focus ring #011132
 *   - Submit: gradient button with hover-shift
 */
const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
  </svg>
);

const EyeSlashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
  </svg>
);

const ExclamationCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
  </svg>
);

const LoginPage: React.FC = () => {
  const [email, setEmail]               = useState('');
  const [password, setPassword]         = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError]               = useState('');
  const [busy, setBusy]                 = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError('Email and password are required.');
      return;
    }
    setError('');
    setBusy(true);
    try {
      await signInWithEmailAndPassword(auth, email.trim().toLowerCase(), password);
      // AuthContext picks up from here.
    } catch (err: any) {
      const code = err?.code as string | undefined;
      let msg = 'Sign-in failed. Please try again.';
      if (code === 'auth/invalid-credential' || code === 'auth/user-not-found') {
        msg = 'Invalid email or password.';
      } else if (code === 'auth/wrong-password') {
        msg = 'Invalid password.';
      } else if (code === 'auth/invalid-email') {
        msg = 'Invalid email format.';
      } else if (code === 'auth/too-many-requests') {
        msg = 'Too many failed attempts. Please try again later.';
      } else if (code === 'auth/network-request-failed') {
        msg = 'Network error. Check your connection and retry.';
      }
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md mx-auto">

        {/* Logo & Branding */}
        <div className="text-center mb-8 space-y-4">
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-xl flex items-center justify-center shadow-lg bg-white border-2" style={{ borderColor: '#011132' }}>
              <img
                src={logoSvg}
                alt="Rascal Republic Logo"
                className="w-12 h-12 object-contain"
              />
            </div>
          </div>
          <div>
            <h1 className="text-3xl font-bold mb-1" style={{ color: '#011132' }}>Company Access Management</h1>
            <p className="text-sm font-medium" style={{ color: '#011132', opacity: 0.7 }}>
              Rascal Republic Group
            </p>
          </div>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">

          {/* Card Header */}
          <div className="p-8 text-white" style={{ background: 'linear-gradient(135deg, #011132 0%, #022050 100%)' }}>
            <h2 className="text-2xl font-bold mb-2">Welcome Back</h2>
            <p className="opacity-90">Sign in to manage company access</p>
          </div>

          {/* Form */}
          <div className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  autoFocus
                  autoComplete="email"
                  placeholder="admin@rascalrepublic.com"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:border-transparent transition-all text-gray-900 placeholder-gray-400"
                  style={{ '--tw-ring-color': '#011132' } as React.CSSProperties}
                  disabled={busy}
                />
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    placeholder="••••••••"
                    className="w-full px-4 py-3 pr-12 rounded-lg border border-gray-300 focus:ring-2 focus:border-transparent transition-all text-gray-900 placeholder-gray-400"
                    style={{ '--tw-ring-color': '#011132' } as React.CSSProperties}
                    disabled={busy}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <span className="text-red-600 flex-shrink-0 mt-0.5"><ExclamationCircleIcon /></span>
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={busy}
                className="w-full py-3 px-4 text-white font-bold rounded-lg shadow-lg transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                style={{
                  backgroundColor: '#011132',
                  backgroundImage: 'linear-gradient(135deg, #011132 0%, #022050 100%)',
                }}
                onMouseEnter={e => {
                  if (!busy) e.currentTarget.style.backgroundImage = 'linear-gradient(135deg, #022050 0%, #033068 100%)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundImage = 'linear-gradient(135deg, #011132 0%, #022050 100%)';
                }}
              >
                {busy ? 'Signing in…' : 'Sign In'}
              </button>
            </form>

            <p className="text-[11px] text-gray-400 text-center mt-6">
              Admin or superadmin role required.
            </p>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-8 text-center pb-4">
          <p className="text-[10px] text-gray-400">
            © 2026 Developed by RHW | Rascal Republic | Samara Lombok | Rinjani Bay Development
          </p>
        </footer>
      </div>
    </div>
  );
};

export default LoginPage;
